import React, {
  ReactElement,
  useState,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react';
import { GameEngine } from 'react-game-engine';
import type YouTubePlayer from 'youtube-player';
import { useLocation } from 'react-router-dom';

import COLORS from '~/constants/colors';

import { Settings, Session } from '../../models';
import { GAME_MODES } from '../../constants/defaults';
import GetReady from './components/GetReady';
import GameForeground from './components/GameForeground';
import VideoBackground from './components/VideoBackground';
import { getCurrentPath } from '../../helpers';
import PATHS from '../../constants/paths';

interface ArcadeProps {
  settings: Settings;
  session: Session;
  videoPlayer: YouTubePlayer;
  setVideoPlayer: Dispatch<SetStateAction<YouTubePlayer>>;
  setPlayerState: Dispatch<SetStateAction<number>>;
}

function Arcade({
  settings,
  session,
  videoPlayer,
  setVideoPlayer,
  setPlayerState,
}: ArcadeProps): ReactElement {
  const [opacity, setOpacity] = useState(1);
  const { state } = useLocation();
  const { gameMode } = state ?? {};
  const song = gameMode === GAME_MODES.play ? settings.song : session.song;
  const gameEngineRef = useRef<GameEngine>(null);
  const currentPath = getCurrentPath();

  // Styling
  const arcadeStyle = {
    display: currentPath === PATHS.arcade ? 'block' : 'none',
    // Needed to hide game excess when zoomed as transition fades
    position: 'fixed' as const, // Prevents input pad touches from dragging game
    top: 0,
    left: 0,
    width: '100%',
    height: 'calc(100vh - calc(100vh - 100%))',
    backgroundColor: COLORS.black,
    // Prevent highlighting issues with pad modes on mobile
    WebkitTouchCallout: 'none' as const,
    WebkitUserSelect: 'none' as const,
    KhtmlUserSelect: 'none' as const,
    MozUserSelect: 'none' as const,
    MsUserSelect: 'none',
    userSelect: 'none' as const,
  };

  return (
    <div style={arcadeStyle}>
      <GetReady opacity={opacity} song={song} />
      <GameForeground
        gameMode={gameMode}
        settings={settings}
        session={session}
        videoPlayer={videoPlayer}
        gameEngineRef={gameEngineRef}
      />
      <VideoBackground
        song={song}
        setOpacity={setOpacity}
        videoPlayer={videoPlayer}
        setVideoPlayer={setVideoPlayer}
        setPlayerState={setPlayerState}
        gameEngineRef={gameEngineRef}
      />
    </div>
  );
}

export default Arcade;
